import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMapEvents } from 'react-leaflet';
import type { LatLngBoundsExpression, GeoJSONOptions } from 'leaflet';
import type { FeatureCollection } from 'geojson';
import { feature } from 'topojson-client';
import type { Metro } from '../../types/metro';
import { MetroBubble } from './MetroBubble';
import { MapZoom } from './MapZoom';
import { CARTO_TILE_REFERRER_POLICY, CARTO_TILE_SUBDOMAINS, VOYAGER_TILE_URL } from '../../lib/tile-url';

interface LeafletMapProps {
  readonly metros: ReadonlyArray<Metro>;
  readonly selectedMetroId: string | null;
  readonly onSelectMetro: (id: string) => void;
  /**
   * Fired with `true` when a metro popup opens and `false` when it
   * closes (its own close button, a click on the map background via
   * Leaflet's closeOnClick, Escape, a programmatic `map.closePopup()`
   * from the Reset-view control, or replacement when the user switches
   * metros). The parent uses this to hide the map legend while a popup
   * is open and bring it back when none is — a signal INDEPENDENT of
   * which metro is selected, so switching from one open popup to another
   * keeps the legend hidden throughout. Optional so the component still
   * works without legend wiring.
   */
  readonly onPopupOpenChange?: (open: boolean) => void;
}

/**
 * Zero-render child that bridges Leaflet's popup open/close map events up
 * to React. Must live inside <MapContainer> so `useMapEvents` can reach
 * the Leaflet instance. Kept as a tiny dedicated component so the popup
 * open-state signal has one obvious home.
 *
 * We track open-state (not selection) because the legend's real
 * requirement is "is a popup covering the map right now" — decoupling it
 * from `selectedMetroId` avoids the switch-between-metros bug where the
 * old popup's `popupclose` would otherwise clear the newly-selected
 * metro. When the user switches A -> B, Leaflet fires `popupclose` (A)
 * then `popupopen` (B) synchronously; React batches both, so the net
 * state stays `open` and the legend never flashes back.
 */
function PopupStateWatcher({
  onPopupOpenChange,
}: {
  readonly onPopupOpenChange?: (open: boolean) => void;
}) {
  useMapEvents({
    popupopen: () => onPopupOpenChange?.(true),
    popupclose: () => onPopupOpenChange?.(false),
  });
  return null;
}

/**
 * Map bounds — split into TWO constants on 2026-05-25 to fix a visual
 * regression introduced by luis-retro-fix-1 (PR #42).
 *
 * - DEFAULT_FIT_BOUNDS = where the camera lands on first load + when the
 *   user clicks Reset View. Tight contiguous-US box (San Diego in the SW,
 *   northern Maine in the NE). This is what users actually want to see.
 *
 * - MAX_BOUNDS = the panning envelope. Wider — extends west to the
 *   Aleutians and north past Alaska so a future dataset that adds AK/HI
 *   metros can still be panned to without us re-widening the constants
 *   in a hurry. Today the dataset is 100% contiguous US (55 metros, all
 *   in lat 25.7-47.6, lng -122.7 to -71.1 — verified 2026-05-25).
 *
 * Why split: PR #42 widened the single US_BOUNDS literal to honor the
 * PDF directive "US-focused view including Alaska + Hawaii", but used
 * it for BOTH fitBounds and maxBounds. On wide screens, fitting to the
 * AK/HI-inclusive envelope zoomed out so far that all of North America
 * + Greenland + UK + Europe + Africa fit in the viewport, and the
 * world overlay polygon produced a horizontal dark banding artifact
 * at high latitudes. Splitting fitBounds (tight) from maxBounds
 * (loose) resolves both symptoms.
 *
 * Future risk: if Georgetown adds AK/HI metros, the default fit will
 * need re-evaluation — the standard treatment is inset boxes for
 * AK/HI rather than literal geographic bounds.
 */
// TODO(V2-map-ak-hi-inset, 2026-07-03): Georgetown reviewers flagged
// that Alaska and Hawaii are visually excluded from the map. The 55
// metro list doesn't currently include any AK/HI metros so nothing
// is being dropped on the floor, but the map still reads as
// "continental US only." Standard treatment is an inset box for
// AK/HI in the bottom-left corner (see e.g. Opportunity Atlas).
// Blocked on:
//   (a) confirmation from Georgetown on whether the metro set will
//       ever include AK/HI; if never, we can add a short "coverage:
//       50 contiguous states" caption instead.
//   (b) design decision: inline SVG insets vs. two extra Leaflet
//       MapContainers stacked in the corner.
const DEFAULT_FIT_BOUNDS: LatLngBoundsExpression = [
  [26, -123], // SW corner (San Diego latitude / just west of Sacramento)
  [49, -68],  // NE corner (Vancouver latitude / Maine coast)
];

/**
 * Options applied to every fitBounds call that targets DEFAULT_FIT_BOUNDS
 * (initial load + Reset View). Tightened on 2026-05-25 follow-up to PR #49:
 *
 * - padding [10, 10]: PR #49 used [40, 40], which on wide-desktop viewports
 *   added ~16% extra visible area on each side. With the contiguous-US
 *   aspect ratio (~2.4:1) and a wider viewport (~3:1 typical), Leaflet
 *   was zooming out to fit the lat range and over-filling lng, leaving
 *   Canadian Arctic visible at the top and the Caribbean at the bottom.
 * - maxZoom 5: hard upper bound on how far OUT the camera can zoom for
 *   the default fit. Makes the framing deterministic across viewport
 *   widths — the map lands at the same scale on a laptop and a 4K
 *   monitor, only the trim changes.
 */
const DEFAULT_FIT_OPTIONS = { padding: [10, 10] as [number, number], maxZoom: 5 };

// BUG-004 (2026-06-01): tightened the eastern edge from -65 to -66 because
// -65 sat ~2° east of the Maine coast (easternmost US point ~-66.95) and
// allowed the panning envelope to include open Atlantic. Maine's coast is
// the practical eastern limit until/unless we add Puerto Rico bubbles. The
// north/west bounds stay loose for the documented future-AK/HI use case.
const MAX_BOUNDS: LatLngBoundsExpression = [
  [18, -170], // SW corner (south of Hawaii, west of the Aleutians)
  [72, -66],  // NE corner (north of Alaska, eastern Maine coast)
];

// Tile URL, subdomains + referrerPolicy live in `lib/tile-url.ts` (CR-033/040:
// CARTO now requires a free key — appended there from VITE_CARTO_KEY).
const TILE_ATTRIBUTION =
  '&copy; <a href="https://carto.com/">CARTO</a> | &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

/** TopoJSON of world country borders — used to gray out non-US countries. */
const WORLD_TOPOJSON_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const US_COUNTRY_ID = '840';

// 2026-05-25 latent-bug note: at very wide zooms the projected non-US
// polygon can show a horizontal dark banding artifact at high northern
// latitudes (~50-67°N) where the world-atlas 110m polygon has dense
// vertices and overlap near the antimeridian. With minZoom=4 (BUG-004
// fix, 2026-06-01) the user can no longer reach the zoom level where
// this becomes visible — keeping the note in case we ever lower minZoom.
// If/when we revisit, the cleanest fixes are: (a) clip the non-US
// polygon to the panning envelope, or (b) drop the overlay in favor of
// a tinted basemap.
const NON_US_STYLE: GeoJSONOptions['style'] = () => ({
  fillColor: '#b0b8c9',
  fillOpacity: 0.55,
  color: '#b0b8c9',
  weight: 0.5,
});

/**
 * Top-level Leaflet map wrapper. Renders the tile base, the non-US gray-out
 * overlay (loaded asynchronously), and one MetroBubble per metro. Unmount
 * cleanup is handled implicitly by react-leaflet — the MapContainer detaches
 * the underlying Leaflet instance when its DOM node leaves the tree.
 *
 * The container needs an explicit width/height in CSS — the parent is
 * responsible for sizing it.
 */
export function LeafletMap({ metros, selectedMetroId, onSelectMetro, onPopupOpenChange }: LeafletMapProps) {
  const [worldGeo, setWorldGeo] = useState<FeatureCollection | null>(null);

  // Fetch the non-US gray-out polygons after first paint. Failure is non-fatal
  // — the map still works, just without the country overlay.
  useEffect(() => {
    let cancelled = false;
    fetch(WORLD_TOPOJSON_URL)
      .then((r) => r.json())
      .then((world) => {
        if (cancelled) return;
        // topojson typings are loose; cast through unknown.
        const fc = feature(world, world.objects.countries) as unknown as FeatureCollection;
        const nonUs: FeatureCollection = {
          type: 'FeatureCollection',
          features: fc.features.filter((f) => String(f.id) !== US_COUNTRY_ID),
        };
        setWorldGeo(nonUs);
      })
      .catch(() => {
        // Silent — overlay is decorative.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      role="application"
      aria-label="Interactive map of metro area credential alignment"
      style={{ width: '100%', height: '100%' }}
    >
    <MapContainer
      bounds={DEFAULT_FIT_BOUNDS}
      boundsOptions={DEFAULT_FIT_OPTIONS}
      // 2026-05-18 feedback: lock the viewport to the US. `maxBounds`
      // stops panning past the edges; `maxBoundsViscosity={1.0}`
      // makes the boundary fully sticky (no rubber-band overscroll).
      // 2026-05-25: `bounds` uses DEFAULT_FIT_BOUNDS (tight contiguous US)
      // so the initial camera lands on the data. `maxBounds` uses the
      // wider MAX_BOUNDS envelope to allow future AK/HI panning.
      maxBounds={MAX_BOUNDS}
      maxBoundsViscosity={1.0}
      // BUG-004 (2026-06-01): minZoom raised from 3 to 4. Leaflet's
      // maxBounds only constrains the camera CENTER, not the visible
      // viewport — at zoom 3 a typical desktop viewport (~1280px wide,
      // ~225° of longitude visible) could show the Atlantic + Pacific
      // even though the center was clamped. zoom 4 keeps the contiguous
      // US filling the viewport so the user can't pan/zoom into ocean.
      minZoom={4}
      zoomControl={false}
      attributionControl={true}
      style={{ width: '100%', height: '100%' }}
    >
      <TileLayer
        url={VOYAGER_TILE_URL}
        attribution={TILE_ATTRIBUTION}
        subdomains={CARTO_TILE_SUBDOMAINS}
        // CR-040: tiles send our origin so the domain-restricted key works.
        referrerPolicy={CARTO_TILE_REFERRER_POLICY}
        maxZoom={19}
        // `noWrap` stops the basemap from repeating horizontally, but on its
        // own it does NOT stop Leaflet's GridLayer from requesting edge tiles
        // whose column index falls outside the valid range at low zoom — that
        // produced 4 CARTO 400s per load at negative column
        // (`/rastertiles/voyager/4/-1/{4,5,6,7}@2x.png`). `bounds` restricts
        // tile requests to the US envelope so those out-of-range columns are
        // never fetched. (MAX_BOUNDS is the same US panning envelope the
        // MapContainer clamps to.)
        noWrap
        bounds={MAX_BOUNDS}
      />
      {worldGeo && <GeoJSON data={worldGeo} style={NON_US_STYLE} interactive={false} />}
      {metros.map((metro) => (
        <MetroBubble
          key={metro.id}
          metro={metro}
          isSelected={metro.id === selectedMetroId}
          onSelect={onSelectMetro}
        />
      ))}
      {/* Custom zoom controls — replaces Leaflet's default
          .leaflet-control-zoom (hidden via globals.css). Must be a child
          of <MapContainer> so its useMap() hook can reach the instance. */}
      <MapZoom defaultBounds={DEFAULT_FIT_BOUNDS} />
      <PopupStateWatcher onPopupOpenChange={onPopupOpenChange} />
    </MapContainer>
    </div>
  );
}
